import * as moment from 'moment-timezone';
import { Nullable } from 'src/core/common/domain/types/types';
import { DateRange } from 'src/core/master-data/domain/date-range';
import { TenantTimezone } from './tenant-timezone';
import { DeviceTimezone } from './device-timezone';

export class LocalDate {
  readonly date: moment.Moment;
  readonly atom: string;
  readonly ymd: string;
  readonly dmy: string;
  readonly timestamp: string;

  // The zone this date is read and rendered in. It defaults to the tenant's, which is what every
  // date in the app used before tasks started carrying one of their own.
  readonly timezone: string;

  private constructor(date?: string, timezone: string = TenantTimezone.current()) {
    // moment.tz reads an input without offset as wall-clock time in that zone, and respects the
    // offset when the input carries one. Parsing with plain moment() would read a day-only string
    // as midnight on the device, which lands on another day outside the tenant's zone.
    this.timezone = timezone;
    this.date = !date ? moment.tz(timezone) : moment.tz(date, timezone);
    this.atom = this.date.format();
    this.ymd = this.date.format('YYYY-MM-DD');
    this.dmy = this.date.format('DD/MM/YYYY');
    this.timestamp = this.date.format('x');
  }

  static now(timezone?: string) {
    return new LocalDate(undefined, timezone);
  }

  static fromIsoString(atom: string, timezone?: string): LocalDate | null {
    if (atom === '0001-01-01T00:00:00') {
      return null;
    }

    return new LocalDate(atom, timezone);
  }

  static fromFormat(date: string, format: string, timezone: string = TenantTimezone.current()): LocalDate | null {
    // Callers pass a day the user picked ('YYYY-MM-DD', 'DD/MM/YYYY'), so it is read as that day in
    // the tenant's zone. Reading it on the device instead would shift the picked day elsewhere.
    const _d = moment.tz(date, format, timezone);
    if (!_d.isValid()) {
      return null;
    }

    return LocalDate.fromIsoString(_d.format(), timezone);
  }

  static fromTimestamp(timestamp: number) {
    const atom = moment(timestamp).format();
    return new LocalDate(atom);
  }

  static fromString(input?: string, timezone?: string): Nullable<LocalDate> {
    if (!input) {
      return null;
    }

    return new LocalDate(input, timezone);
  }

  // Instants are compared as moments, never as atom strings: two atoms for the same zone carry
  // different offsets across a DST change, and lexicographic order then contradicts chronology.
  isBefore(other: LocalDate): boolean {
    return this.date.isBefore(other.date);
  }

  isAfter(other: LocalDate): boolean {
    return this.date.isAfter(other.date);
  }

  isSameOrBefore(other: LocalDate): boolean {
    return this.date.isSameOrBefore(other.date);
  }

  isSameOrAfter(other: LocalDate): boolean {
    return this.date.isSameOrAfter(other.date);
  }

  inRange(range: DateRange): boolean {
    return this.isSameOrAfter(range.from) && this.isSameOrBefore(range.to);
  }

  /**
   * Whether the hour this date paints is not the hour the device's own clock shows for the same
   * instant. Offsets are compared, never zone names: Europe/Lisbon and Atlantic/Canary are two names
   * for the same clock, and telling them apart would be noise.
   *
   * The comparison happens at this date's instant, not at now(), because two zones can share an
   * offset for part of the year and part ways for the rest.
   */
  readsDifferentlyOnDevice(): boolean {
    const onDevice = this.date.clone().tz(DeviceTimezone.current());

    return this.date.utcOffset() !== onDevice.utcOffset();
  }

  toFormat(format: string) {
    return this.date.format(format);
  }

  formatToDMYDynamic(): string {
    const today = LocalDate.now().date;
    if (today.isSame(this.date, 'year')) {
      return this.date.format('DD/MM');
    }

    return this.date.format('DD/MM/YY');
  }

  discardTime() {
    const date = this.date.clone();
    date.hours(0).minutes(0).seconds(0);
    return new LocalDate(date.format(), this.timezone);
  }
}
